import axios from "axios";
import { getCSRFToken, checkRateLimit, validateURL } from '../utils/security';
import { apiRateLimiter } from '../utils/rateLimiter';
import { DEV_CONFIG, mockApiResponses, checkBackendHealth } from '../config/development';

// Use environment variable for better flexibility
const baseURL = import.meta.env.VITE_API_URL || "https://api.sewaapartementheedgebylia.my.id/api";

console.log('API Base URL:', baseURL);

// Track backend availability
let backendAvailable = true;

const api = axios.create({
  baseURL,
  timeout: 5000, // Reduced timeout to 5 seconds for faster response
  headers: {
    'Accept': 'application/json'
  },
  validateStatus: (status) => status < 500, // Don't reject on 4xx errors
  maxContentLength: 50 * 1024 * 1024, // 50MB max
  maxBodyLength: 50 * 1024 * 1024 // 50MB max
});

// Mock API wrapper for development
const mockApi = {
  get: async (url) => {
    console.log('Mock API GET:', url);
    await new Promise(resolve => setTimeout(resolve, 500)); // Simulate network delay

    if (url.includes('/properties')) {
      return { data: mockApiResponses.properties.get() };
    }
    return { data: { message: 'Mock response' } };
  },

  post: async (url, data) => {
    console.log('Mock API POST:', url, data);
    await new Promise(resolve => setTimeout(resolve, 800));

    if (url.includes('/properties')) {
      const formData = data instanceof FormData ? Object.fromEntries(data.entries()) : data;
      return { data: mockApiResponses.properties.post(formData) };
    }
    return { data: { message: 'Created successfully' } };
  },

  put: async (url, data) => {
    console.log('Mock API PUT:', url, data);
    await new Promise(resolve => setTimeout(resolve, 800));

    if (url.includes('/properties/')) {
      const id = url.split('/').pop();
      const formData = data instanceof FormData ? Object.fromEntries(data.entries()) : data;
      return { data: mockApiResponses.properties.put(id, formData) };
    }
    return { data: { message: 'Updated successfully' } };
  },

  patch: async (url, data) => {
    console.log('Mock API PATCH:', url, data);
    await new Promise(resolve => setTimeout(resolve, 500));
    return { data: { success: true, message: 'Patched successfully' } };
  },

  delete: async (url) => {
    console.log('Mock API DELETE:', url);
    await new Promise(resolve => setTimeout(resolve, 500));

    if (url.includes('/properties/')) {
      const id = url.split('/').pop();
      return { data: mockApiResponses.properties.delete(id) };
    }
    return { data: { message: 'Deleted successfully' } };
  }
};

// Enhanced API wrapper with fallback
const enhancedApi = {
  async get(url, config = {}) {
    try {
      return await api.get(url, config);
    } catch (error) {
      if (DEV_CONFIG.ENABLE_MOCK_DATA && (error.code === 'ECONNREFUSED' || error.code === 'ECONNABORTED')) {
        console.warn('Falling back to mock API for GET:', url);
        return await mockApi.get(url);
      }
      throw error;
    }
  },

  async post(url, data, config = {}) {
    try {
      return await api.post(url, data, config);
    } catch (error) {
      if (DEV_CONFIG.ENABLE_MOCK_DATA && (error.code === 'ECONNREFUSED' || error.code === 'ECONNABORTED')) {
        console.warn('Falling back to mock API for POST:', url);
        return await mockApi.post(url, data);
      }
      throw error;
    }
  },

  async put(url, data, config = {}) {
    try {
      return await api.put(url, data, config);
    } catch (error) {
      if (DEV_CONFIG.ENABLE_MOCK_DATA && (error.code === 'ECONNREFUSED' || error.code === 'ECONNABORTED')) {
        console.warn('Falling back to mock API for PUT:', url);
        return await mockApi.put(url, data);
      }
      throw error;
    }
  },

  async patch(url, data, config = {}) {
    try {
      return await api.patch(url, data, config);
    } catch (error) {
      if (DEV_CONFIG.ENABLE_MOCK_DATA && (error.code === 'ECONNREFUSED' || error.code === 'ECONNABORTED')) {
        console.warn('Falling back to mock API for PATCH:', url);
        return await mockApi.patch(url, data);
      }
      throw error;
    }
  },

  async delete(url, config = {}) {
    try {
      return await api.delete(url, config);
    } catch (error) {
      if (DEV_CONFIG.ENABLE_MOCK_DATA && (error.code === 'ECONNREFUSED' || error.code === 'ECONNABORTED')) {
        console.warn('Falling back to mock API for DELETE:', url);
        return await mockApi.delete(url);
      }
      throw error;
    }
  }
};

// Request interceptor with security enhancements
api.interceptors.request.use((config) => {
  // Add authentication token
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Remove timestamp - allow browser caching for better performance
  // Only add cache-busting for specific endpoints if needed

  // Add CSRF token for state-changing requests
  if (['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase())) {
    const csrfToken = getCSRFToken();
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
  }

  // Security headers
  config.headers['Accept'] = 'application/json';
  config.headers['X-Requested-With'] = 'XMLHttpRequest';

  // Set Content-Type only for non-FormData requests
  if (config.data && !(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }

  // Validate URL for external requests (skip for local development)
  if (config.url && config.url.startsWith('http') && process.env.NODE_ENV === 'production' && !validateURL(config.url)) {
    return Promise.reject(new Error('Invalid or unsafe URL'));
  }

  return config;
}, (error) => {
  return Promise.reject(error);
});

// Response interceptor with security checks
api.interceptors.response.use(
  (response) => {
    // Log successful requests for monitoring
    if (import.meta.env.DEV) {
      console.log(`API Success: ${response.config.method?.toUpperCase()} ${response.config.url}`);
    }
    return response;
  },
  (error) => {
    // Enhanced error handling
    if (error.response) {
      const { status, data } = error.response;

      // Handle specific error cases
      switch (status) {
        case 401:
          // Clear invalid tokens
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          break;
        case 403:
          console.warn('Access forbidden:', data?.message);
          break;
        case 429:
          console.warn('Rate limit exceeded');
          break;
        case 500:
          console.error('Server error:', data?.message);
          break;
      }
    } else if (error.request) {
      // Retry on timeout or network errors with faster retry
      if (error.code === 'ECONNABORTED' && !error.config.__retryCount) {
        error.config.__retryCount = 1;
        console.log('Retrying due to timeout...');
        return new Promise(resolve => {
          setTimeout(() => resolve(api(error.config)), 500); // Reduced retry delay to 500ms
        });
      }

      if (error.code !== 'ECONNABORTED') {
        console.error('Network error:', error.message);
      }
    }

    return Promise.reject(error);
  }
);

// Initialize CSRF token on app start
if (typeof window !== 'undefined') {
  import('../utils/security').then(({ setCSRFToken }) => {
    setCSRFToken();
  });
}

export default enhancedApi;
